import React, { useEffect, useState } from 'react';
import { FieldProps } from '@rjsf/core';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';

/**
 * ClusterMappingDomainPicker
 * 
 * 自定义字段：从 cluster-mapping.yaml 动态加载 domain 列表
 * 
 * 使用方式：
 * ```yaml
 * domain:
 *   title: 业务域
 *   type: string
 *   ui:field: ClusterMappingDomainPicker
 * ```
 */
export const ClusterMappingDomainPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  uiSchema,
  idSchema,
  schema,
}: FieldProps<string>) => {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  
  const [domains, setDomains] = useState<Array<{ id: string; name: string; description: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    const loadDomains = async () => {
      try {
        setLoading(true);
        setError(undefined);

        const backendUrl = await discoveryApi.getBaseUrl('scaffolder');
        const { token } = await identityApi.getCredentials();

        const response = await fetch(`${backendUrl}/cluster-mapping`, {
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load domains: ${response.statusText}`);
        }

        const mappings = await response.json();

        // 转换为选项列表
        const domainList = Object.entries(mappings).map(([key, value]: [string, any]) => ({
          id: key,
          name: key.toUpperCase(),
          description: value.description || '',
        }));

        setDomains(domainList);
      } catch (err) {
        console.error('Failed to load cluster mapping domains:', err);
        setError(err instanceof Error ? err.message : 'Failed to load domains');
      } finally {
        setLoading(false);
      }
    };

    loadDomains();
  }, [discoveryApi, identityApi]);

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
      fullWidth
    >
      <InputLabel htmlFor={idSchema.$id}>
        {schema.title || 'Domain'}
      </InputLabel>
      <Select
        id={idSchema.$id}
        value={formData || ''}
        onChange={e => onChange(e.target.value as string)}
        disabled={loading}
      >
        {loading && (
          <MenuItem value="" disabled>
            加载中...
          </MenuItem>
        )}
        {error && (
          <MenuItem value="" disabled>
            加载失败: {error}
          </MenuItem>
        )}
        {!loading && !error && domains.length === 0 && (
          <MenuItem value="" disabled>
            无可用域
          </MenuItem>
        )}
        {domains.map(domain => (
          <MenuItem key={domain.id} value={domain.id}>
            {domain.name} - {domain.description}
          </MenuItem>
        ))}
      </Select>
      {uiSchema?.['ui:help'] && (
        <FormHelperText>{uiSchema['ui:help']}</FormHelperText>
      )}
      {rawErrors?.length > 0 && !formData && (
        <FormHelperText error>此字段为必填项</FormHelperText>
      )}
    </FormControl>
  );
};

